/**
 * Wiedergabe der erzeugten Signale.
 *
 * Die Abtastwerte kommen aus `synth`, hier werden sie in einen AudioBuffer gefüllt und
 * abgespielt. Der AudioContext entsteht erst beim ersten Klick, weil Browser ihn ohne
 * Zutun des Nutzers angehalten lassen, und es läuft immer nur ein Signal: wer ein zweites
 * startet, bricht das erste ab.
 */
import type { SignalData } from '@/core/schema'
import { timeline, type SignalStep } from '@/core/signals'
import type { Whistle } from '@/core/whistle'
import { renderSteps } from './synth'

let context: AudioContext | undefined
let current: AudioBufferSourceNode | undefined

function audioContext(): AudioContext {
  context ??= new AudioContext()
  return context
}

export function isSupported(): boolean {
  return typeof AudioContext !== 'undefined'
}

/**
 * Spielt ein Signal und liefert, wenn es zu Ende ist. Ein Abbruch durch `stop` oder durch
 * das nächste Signal liefert ebenfalls, damit die Anzeige nicht hängen bleibt.
 */
export async function play(signal: SignalData, voice?: Whistle): Promise<void> {
  return playSteps(timeline(signal), voice)
}

/** Spielt eine fertige Zeitleiste, etwa ein Schallzeichen der Binnenschifffahrt. */
export async function playSteps(steps: SignalStep[], voice?: Whistle): Promise<void> {
  stop()
  const ctx = audioContext()
  if (ctx.state === 'suspended') await ctx.resume()
  const samples = renderSteps(steps, { sampleRate: ctx.sampleRate, voice })
  const buffer = ctx.createBuffer(1, samples.length, ctx.sampleRate)
  buffer.copyToChannel(samples, 0)
  const source = ctx.createBufferSource()
  source.buffer = buffer
  source.connect(ctx.destination)
  current = source
  await new Promise<void>((resolve) => {
    source.onended = () => {
      if (current === source) current = undefined
      resolve()
    }
    source.start()
  })
}

export function stop(): void {
  if (!current) return
  const source = current
  current = undefined
  source.onended = null
  source.stop()
  source.disconnect()
}
